import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { SITE_URL, alternatesFor, normalizePath } from '../lib/routes'

// The one canonical link of every page: the page's own URL. Mounted once in
// Router (beside HreflangManager), so every route — home, service, corporate
// and blog pages in all three locales — gets exactly one, and the prerender
// snapshots it. Nothing else writes a canonical: not index.html, not SEO.jsx,
// not useServiceSeo. A path outside the route table (an unknown route or
// blog slug) gets none; a case variant (/Kurumsal) gets the table's spelling.
//
// Written straight into <head>, not through Helmet: under StrictMode (dev)
// Helmet drops the <link> tags it renders, which would hide the canonical
// from the dev-server checks while the build still had it.

/**
 * @param {string} pathname
 * @returns {string | null} the canonical URL, or null off the route table
 */
function canonicalFor(pathname) {
    const path = normalizePath(pathname).toLowerCase()
    const group = alternatesFor(path)
    const own = group && Object.values(group).find((candidate) => candidate === path)
    return own ? `${SITE_URL}${own}` : null
}

export default function CanonicalManager() {
    const { pathname } = useLocation()
    const href = canonicalFor(pathname)

    useEffect(() => {
        // Keep one tag (the prerendered one when there is one), drop any other.
        const [existing, ...extras] = document.head.querySelectorAll('link[rel="canonical"]')
        extras.forEach((tag) => tag.remove())
        if (!href) {
            existing?.remove()
            return
        }
        const tag = existing ?? document.createElement('link')
        tag.rel = 'canonical'
        tag.href = href
        if (!existing) document.head.appendChild(tag)
    }, [href])

    return null
}
